/* Modal de respuesta OBLIGATORIA de asistencia a un evento.
 *
 * Comportamiento (misma regla que la actualización de datos / evaluación):
 * - Al iniciar sesión consulta /eventos/respuesta/pendiente. Si el usuario
 *   tiene una respuesta pendiente en un evento vigente con
 *   respuesta_obligatoria = 1 (y la ventana de confirmación abierta), abre el
 *   modal pidiendo que responda si asistirá o no. Se puede SALTAR.
 * - Cuando entra a /salarios, /historial-de-trabajo o /reclamos con una
 *   respuesta pendiente, el padre activa `bloqueado`: "Saltar" lo devuelve al
 *   inicio (no puede acceder a esas secciones sin responder).
 * - Eventos con pedido: al responder que SÍ, el modal pasa a un segundo paso
 *   con el catálogo de ítems (cantidades, cuotas y consentimiento) para armar
 *   el pedido ahí mismo; también puede asistir sin pedido y armarlo/editarlo
 *   después desde la card del Home (hasta el fin de confirmación).
 *
 * Robustez (visto en producción móvil): las respuestas del endpoint se
 * descartan si llegó una consulta más nueva (evita que una respuesta vieja o
 * lenta pise el estado), y ante error de red se reintenta una vez en lugar de
 * asumir "sin pendiente".
 */
import {
  IonButton,
  IonButtons,
  IonCheckbox,
  IonContent,
  IonHeader,
  IonModal,
  IonTitle,
  IonToolbar,
  useIonToast,
} from "@ionic/react";
import { CircularProgress, Typography } from "@material-ui/core";
import { useEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import Eventos, { EventoItem } from "src/services/eventos";

const fmtFecha = (iso?: string) => {
  if (!iso) return "—";
  const d = new Date(String(iso).replace(" ", "T"));
  if (isNaN(d.getTime())) return String(iso);
  return d.toLocaleString("es-PY", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const fmtMonto = (n: number | null | undefined) =>
  `Gs. ${Number(n || 0).toLocaleString("es-PY", { maximumFractionDigits: 0 })}`;

interface Props {
  isAuth: boolean;
  onPendienteChange?: (pendiente: number) => void; // 0 | 1
  bloqueado?: boolean;
  solicitudReapertura?: number;
}

const EventoRespuestaModal: React.FC<Props> = ({
  isAuth,
  onPendienteChange,
  bloqueado = false,
  solicitudReapertura = 0,
}) => {
  const [present] = useIonToast();
  const history = useHistory();

  const [abierto, setAbierto] = useState(false);
  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [invitacion, setInvitacion] = useState<any | null>(null);
  const [acomp, setAcomp] = useState(0);

  // Paso de pedido (eventos con requiere_pedido, tras responder que SÍ).
  const [paso, setPaso] = useState<"respuesta" | "pedido">("respuesta");
  const [pedidoInfo, setPedidoInfo] = useState<Awaited<ReturnType<typeof Eventos.obtenerPedido>> | null>(null);
  const [cantidades, setCantidades] = useState<Record<number, number>>({});
  const [cuotasSel, setCuotasSel] = useState(1);
  const [aceptaDescuento, setAceptaDescuento] = useState(false);

  const yaVerificado = useRef(false);
  // Secuencia de consultas: descarta respuestas viejas que lleguen tarde.
  const reqSeqRef = useRef(0);
  const retryRef = useRef<number | null>(null);

  const setPendiente = (n: number) => onPendienteChange?.(n);

  const cargarPendiente = async (esReintento = false) => {
    const seq = ++reqSeqRef.current;
    if (retryRef.current) {
      window.clearTimeout(retryRef.current);
      retryRef.current = null;
    }
    setCargando(true);
    try {
      const { bloqueado: hayPendiente, invitacion: inv } = await Eventos.respuestaPendiente();
      if (seq !== reqSeqRef.current) return; // llegó tarde: ya hay una consulta más nueva
      setPendiente(hayPendiente ? 1 : 0);
      if (!hayPendiente || !inv) {
        setAbierto(false);
        return;
      }
      setInvitacion(inv);
      setAcomp(0);
      setPaso("respuesta");
      setAbierto(true);
    } catch {
      if (seq !== reqSeqRef.current) return;
      // Error de red (frecuente en móvil): NO pisamos el estado conocido y
      // reintentamos una única vez. Si vuelve a fallar, no bloqueamos.
      if (!esReintento) {
        retryRef.current = window.setTimeout(() => cargarPendiente(true), 4000);
      }
    } finally {
      if (seq === reqSeqRef.current) setCargando(false);
    }
  };

  // Verificación inicial al autenticarse.
  useEffect(() => {
    if (!isAuth) {
      yaVerificado.current = false;
      setAbierto(false);
      setInvitacion(null);
      setPendiente(0);
      return;
    }
    if (yaVerificado.current) return;
    yaVerificado.current = true;
    cargarPendiente();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuth]);

  // Reapertura forzada al entrar a una ruta bloqueada.
  useEffect(() => {
    if (!isAuth || solicitudReapertura === 0) return;
    if (abierto) return;
    cargarPendiente();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [solicitudReapertura]);

  useEffect(() => () => {
    if (retryRef.current) window.clearTimeout(retryRef.current);
  }, []);

  const handleSaltar = () => {
    setAbierto(false);
    if (bloqueado) history.replace("/home");
  };

  // ------------------------- Paso de pedido -------------------------
  const irAPedido = async () => {
    if (!invitacion) return;
    setGuardando(true);
    try {
      const info = await Eventos.obtenerPedido(invitacion.evento_id);
      setPedidoInfo(info);
      const cant: Record<number, number> = {};
      (info.pedido || []).forEach((l) => (cant[l.item_id] = l.cantidad));
      setCantidades(cant);
      setCuotasSel(info.cuotas ?? 1);
      setAceptaDescuento(Boolean(info.consentimiento_fecha));
      setPaso("pedido");
    } catch {
      // Si el catálogo no carga, confirmamos asistencia simple para no trabar.
      await responder(true);
    } finally {
      setGuardando(false);
    }
  };

  const setCantidad = (item: EventoItem, delta: number) => {
    setCantidades((prev) => {
      const actual = prev[item.id!] ?? 0;
      let nuevo = actual + delta;
      if (nuevo < 0) nuevo = 0;
      if (item.disponibles !== null && item.disponibles !== undefined && nuevo > item.disponibles) {
        nuevo = item.disponibles;
      }
      return { ...prev, [item.id!]: nuevo };
    });
  };

  const totalUnidades = Object.values(cantidades).reduce((a, b) => a + b, 0);
  const totalPedido = (pedidoInfo?.items || []).reduce(
    (acc, it) => acc + (cantidades[it.id!] ?? 0) * Number(it.precio || 0),
    0
  );

  const confirmarPedido = async () => {
    if (!invitacion) return;
    if (totalUnidades === 0) {
      present({ message: "Elegí al menos un ítem (o asistí sin pedido)", duration: 3000, color: "warning" });
      return;
    }
    if (!aceptaDescuento) {
      present({ message: "Debés aceptar el descuento de sueldo para confirmar el pedido", duration: 3500, color: "warning" });
      return;
    }
    setGuardando(true);
    try {
      const items = Object.entries(cantidades)
        .filter(([, cant]) => cant > 0)
        .map(([itemId, cant]) => ({ item_id: Number(itemId), cantidad: cant }));
      await Eventos.guardarPedido(invitacion.evento_id, items, true, acomp, cuotasSel);
      present({
        message: `¡Pedido confirmado! Total: ${fmtMonto(totalPedido)}${cuotasSel > 1 ? ` en ${cuotasSel} cuotas` : ""}`,
        duration: 3500,
        color: "success",
      });
      await cargarPendiente();
    } catch (err: any) {
      present({
        message: err?.response?.data?.error || "No se pudo guardar el pedido",
        duration: 4000,
        color: "danger",
      });
    } finally {
      setGuardando(false);
    }
  };

  // ------------------------- Respuesta sí/no -------------------------
  const responder = async (asistira: boolean) => {
    if (!invitacion) return;
    setGuardando(true);
    try {
      await Eventos.confirmar(invitacion.evento_id, asistira, asistira ? acomp : 0);
      present({
        message: asistira ? "¡Asistencia confirmada!" : "Registrado: no asistirás",
        duration: 3500,
        color: "success",
      });
      // Puede haber otra invitación obligatoria pendiente: re-verificamos
      // (cierra solo si no queda ninguna).
      await cargarPendiente();
    } catch (err: any) {
      present({
        message: err?.response?.data?.error || "No se pudo registrar la respuesta",
        duration: 4000,
        color: "danger",
      });
    } finally {
      setGuardando(false);
    }
  };

  // "Sí, voy a asistir": en eventos con pedido pasa al catálogo; si no, confirma directo.
  const handleSi = () => {
    if (invitacion && Boolean(invitacion.requiere_pedido)) {
      irAPedido();
    } else {
      responder(true);
    }
  };

  return (
    <IonModal isOpen={abierto} backdropDismiss={false}>
      <IonHeader>
        <IonToolbar>
          <IonTitle style={{ fontSize: 16 }}>
            {paso === "pedido" ? "Armá tu pedido" : "Invitación a evento"}
          </IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={handleSaltar} disabled={guardando}>
              Saltar
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        {cargando || guardando ? (
          <div style={{ display: "flex", justifyContent: "center", padding: 32 }}>
            <CircularProgress />
          </div>
        ) : invitacion && paso === "pedido" && pedidoInfo ? (
          <div style={{ padding: 16 }}>
            <Typography variant="h6" style={{ fontWeight: 700 }}>
              {invitacion.tema}
            </Typography>
            <Typography variant="caption" color="textSecondary" style={{ display: "block", marginBottom: 8 }}>
              Elegí tus ítems. Podés editar el pedido hasta el {fmtFecha(invitacion.fecha_fin_confirmacion)}.
            </Typography>

            {pedidoInfo.items.map((it) => {
              const cant = cantidades[it.id!] ?? 0;
              const agotado =
                it.disponibles !== null && it.disponibles !== undefined && it.disponibles <= 0 && cant === 0;
              return (
                <div
                  key={it.id}
                  style={{ border: "1px solid #eee", borderRadius: 8, padding: "8px 10px", marginTop: 8, opacity: agotado ? 0.55 : 1 }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <Typography variant="body2" style={{ fontWeight: 700 }}>{it.nombre}</Typography>
                      {it.descripcion && (
                        <Typography variant="caption" color="textSecondary">{it.descripcion}</Typography>
                      )}
                      <Typography variant="body2">{fmtMonto(it.precio)}</Typography>
                      {agotado && (
                        <Typography variant="caption" style={{ color: "#c62828", fontWeight: 700 }}>AGOTADO</Typography>
                      )}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <IonButton size="small" fill="outline" disabled={cant === 0} onClick={() => setCantidad(it, -1)}>−</IonButton>
                      <span style={{ minWidth: 20, textAlign: "center", fontWeight: 700 }}>{cant}</span>
                      <IonButton
                        size="small"
                        fill="outline"
                        disabled={agotado || (it.disponibles !== null && it.disponibles !== undefined && cant >= it.disponibles)}
                        onClick={() => setCantidad(it, 1)}
                      >+</IonButton>
                    </div>
                  </div>
                </div>
              );
            })}

            <div style={{ marginTop: 10, background: "#f5f5f5", borderRadius: 8, padding: 10, display: "flex", justifyContent: "space-between" }}>
              <Typography variant="body2" style={{ fontWeight: 700 }}>Total</Typography>
              <Typography variant="body2" style={{ fontWeight: 700 }}>{fmtMonto(totalPedido)}</Typography>
            </div>

            {pedidoInfo.permite_cuotas && totalUnidades > 0 && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
                <span style={{ fontSize: 14 }}>Descontar en:</span>
                <select
                  value={cuotasSel}
                  onChange={(e) => setCuotasSel(Number(e.target.value))}
                  style={{ fontSize: 15, padding: "6px 10px", border: "1px solid #c4c4c4", borderRadius: 6, background: "#fff", color: "#000" }}
                >
                  {Array.from({ length: pedidoInfo.max_cuotas ?? 1 }, (_, i) => i + 1).map((n) => (
                    <option key={n} value={n}>{n === 1 ? "1 cuota" : `${n} cuotas`}</option>
                  ))}
                </select>
                {cuotasSel > 1 && (
                  <span style={{ fontSize: 13, color: "#666" }}>
                    aprox. {fmtMonto(Math.round(totalPedido / cuotasSel))} por cuota
                  </span>
                )}
              </div>
            )}

            {totalUnidades > 0 && (
              <label style={{ display: "flex", alignItems: "flex-start", gap: 8, marginTop: 10, fontSize: 13, color: "#444" }}>
                <IonCheckbox
                  checked={aceptaDescuento}
                  onIonChange={(e) => setAceptaDescuento(e.detail.checked)}
                  style={{ flexShrink: 0, width: 24, height: 24, minWidth: 24, marginTop: 2, "--size": "24px" } as any}
                />
                <span>
                  Acepto que el total de <strong>{fmtMonto(totalPedido)}</strong>
                  {cuotasSel > 1 ? <> (en <strong>{cuotasSel} cuotas</strong>)</> : null}{" "}
                  se descuente de mi sueldo <strong>únicamente por lo que retire</strong> el día del evento.
                </span>
              </label>
            )}

            <IonButton expand="block" color="success" style={{ marginTop: 14 }} disabled={totalUnidades === 0} onClick={confirmarPedido}>
              Confirmar pedido ({fmtMonto(totalPedido)})
            </IonButton>
            <IonButton expand="block" fill="outline" color="medium" onClick={() => responder(true)}>
              Asistiré sin pedido
            </IonButton>
          </div>
        ) : invitacion ? (
          <div style={{ padding: 16 }}>
            <Typography variant="h6" style={{ fontWeight: 700 }}>
              {invitacion.tema}
            </Typography>
            {invitacion.descripcion && (
              <Typography variant="body2" style={{ marginTop: 8, whiteSpace: "pre-wrap" }}>
                {invitacion.descripcion}
              </Typography>
            )}
            <div style={{ marginTop: 12 }}>
              <Typography variant="body2"><strong>Inicio:</strong> {fmtFecha(invitacion.fecha_inicio_evento)}</Typography>
              <Typography variant="body2"><strong>Fin:</strong> {fmtFecha(invitacion.fecha_fin_evento)}</Typography>
              {invitacion.lugar && (
                <Typography variant="body2"><strong>Lugar:</strong> {invitacion.lugar}</Typography>
              )}
              <Typography variant="body2" color="textSecondary" style={{ marginTop: 6 }}>
                Respondé hasta el {fmtFecha(invitacion.fecha_fin_confirmacion)}
              </Typography>
            </div>

            <div style={{ marginTop: 14, background: "#fff8e1", borderRadius: 8, padding: 10 }}>
              <Typography variant="body2" style={{ fontWeight: 700 }}>
                Este evento requiere que respondas si vas a asistir o no.
              </Typography>
              {bloqueado && (
                <Typography variant="caption" style={{ color: "#c62828", display: "block", marginTop: 4 }}>
                  Para acceder a recibos / historial / reclamos de horas debés responder.
                  Si saltás, volverás al inicio.
                </Typography>
              )}
            </div>

            {(invitacion.max_acompanantes ?? 0) > 0 && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 14 }}>
                <span style={{ fontSize: 14 }}>Acompañantes a llevar:</span>
                <select
                  value={acomp}
                  onChange={(e) => setAcomp(Number(e.target.value))}
                  style={{ fontSize: 15, padding: "6px 10px", border: "1px solid #c4c4c4", borderRadius: 6, background: "#fff", color: "#000" }}
                >
                  {Array.from({ length: (invitacion.max_acompanantes ?? 0) + 1 }, (_, i) => i).map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>
            )}

            <IonButton expand="block" color="success" style={{ marginTop: 16 }} onClick={handleSi}>
              {Boolean(invitacion.requiere_pedido) ? "Sí, voy a asistir → elegir mi pedido" : "Sí, voy a asistir"}
            </IonButton>
            <IonButton expand="block" fill="outline" color="medium" onClick={() => responder(false)}>
              No podré asistir
            </IonButton>
          </div>
        ) : null}
      </IonContent>
    </IonModal>
  );
};

export default EventoRespuestaModal;
